import LockIcon from '@mui/icons-material/Lock';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import VerticalAlignBottomIcon from '@mui/icons-material/VerticalAlignBottom';
import VerticalAlignTopIcon from '@mui/icons-material/VerticalAlignTop';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import {
  FormatAlignCenter,
  FormatAlignLeft,
  FormatAlignRight,
  FormatUnderlined as FormatUnderlinedIcon,
  DeleteOutline,
} from '@mui/icons-material';
import { fabric } from 'fabric';
import { Canvas, Textbox } from 'fabric/fabric-impl';
import { ChangeEvent, MouseEvent as ReactMouseEvent, MutableRefObject, useEffect, useRef, useState } from 'react';
import Loader from '../../../common/loader';
// import { CanvasObject } from '../../types';
import {
  Box,
  Grid,
  IconButton,
  List,
  ListItem,
  Typography,
  TextField,
  Stack,
  Button,
  Slider,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  ListItemText,
  InputAdornment,
} from '@mui/material';
import { CanvasObject } from '../../../../api/digital-signage-cms/types';
import MiniCanvas from './components/MiniCanvas';
import ColorPicker from '../modules/ColorPicker';
import SelectFont from '../common/SelectFont';
import AlertDialog from '../common/AlertDialog';
import { ColorPalette } from '../../types';

const DEFAULT_BRAND_COLOR_PALETTES: ColorPalette[] = [
  {
    title: 'Brand Colors',
    colors: ['#FF6961', '#2A2C31', '#171719', '#8CB9FF', '#9BE7A1', '#FFAD42', '#FFFFFF', '#000000'],
  },
];

const STANDARD_FILL_SWATCHES = ['#000000', '#FFFFFF', '#D32F2F', '#F57C00', '#FBC02D', '#388E3C', '#1976D2', '#7B1FA2'];

const normalizeHex = (value: string) => value.trim().toUpperCase();

const Layers = ({
  canvas,
  colorPalettes: initialColorPalettes,
  setColorPalettes: initialSetColorPalettes,
}: {
  canvas: MutableRefObject<Canvas | undefined>;
  colorPalettes?: ColorPalette[];
  setColorPalettes?: any;
}) => {
  const [shapes, setShapes] = useState<CanvasObject[]>([]);
  const containerRef = useRef<HTMLDivElement>(null);
  const [currentActiveLayer, setCurrentActiveLayer] = useState<number>(-1);
  const [selectedObjectType, setSelectedObjectType] = useState<string>('');
  const [fillColor, setFillColor] = useState<string>('#000000');
  const [strokeColor, setStrokeColor] = useState<string>('#000000');
  const [strokeWidth, setStrokeWidth] = useState<number>(0);
  const [opacity, setOpacity] = useState<number>(100);
  const [cornerRadius, setCornerRadius] = useState<number>(0);
  const [fontSize, setFontSize] = useState<number>(12);
  const [selectedFontIndex, setSelectedFontIndex] = useState(0);
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [fillColorPickerOpen, setFillColorPickerOpen] = useState(false);
  const [strokeColorPickerOpen, setStrokeColorPickerOpen] = useState(false);
  const [selectedLayerIndexes, setSelectedLayerIndexes] = useState<number[]>([]);
  const [editingLayerIndex, setEditingLayerIndex] = useState<number | null>(null);
  const [editingLayerName, setEditingLayerName] = useState<string>('');
  const [draggingLayerIndex, setDraggingLayerIndex] = useState<number | null>(null);
  const [dragOverLayerIndex, setDragOverLayerIndex] = useState<number | null>(null);
  const [dragInsertPosition, setDragInsertPosition] = useState<'before' | 'after'>('after');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  // Use provided props or fallback defaults
  const [colorPalettes, setColorPalettes] = useState(
    initialColorPalettes || DEFAULT_BRAND_COLOR_PALETTES
  );

  const fonts = [
    { name: 'Open Sans', class: 'open-sans' },
    { name: 'EB Garamond', class: 'eb-garamond' },
    { name: 'Jost', class: 'jost' },
    { name: 'Inter', class: 'inter' },
    { name: 'Bodoni Moda', class: 'bodoni-moda' },
    { name: 'Martian Mono', class: 'martian-mono' },
    { name: 'Gothic A1', class: 'gothic-a1' },
    { name: 'Source Serif 4', class: 'source-serif-4' },
    { name: 'Arvo', class: 'arvo' },
    { name: 'Libre Franklin', class: 'libre-franklin' },
    { name: 'Roboto', class: 'roboto' },
    { name: 'La Belle Aurore', class: 'la-belle-aurore' },
  ];

  useEffect(() => {
    let unbind: (() => void) | undefined;
    let waitForCanvasTimer: ReturnType<typeof setInterval> | undefined;

    const bindListeners = (targetCanvas: Canvas) => {
      const onObjectAdded = () => handleFabricObjectAdded();
      const onObjectModified = () => handleModifiedObject();
      const onObjectRemoved = () => handleModifiedObject();
      const onSelectionCreated = () => handleSelectionCreated();
      const onSelectionUpdated = () => handleSelectionCreated();
      const onSelectionCleared = () => handleCanvasCleared();

      targetCanvas.on('object:added', onObjectAdded);
      targetCanvas.on('object:modified', onObjectModified);
      targetCanvas.on('object:removed', onObjectRemoved);
      targetCanvas.on('selection:created', onSelectionCreated);
      targetCanvas.on('selection:updated', onSelectionUpdated);
      targetCanvas.on('selection:cleared', onSelectionCleared);

      handleModifiedObject();

      return () => {
        targetCanvas.off('object:added', onObjectAdded);
        targetCanvas.off('object:modified', onObjectModified);
        targetCanvas.off('object:removed', onObjectRemoved);
        targetCanvas.off('selection:created', onSelectionCreated);
        targetCanvas.off('selection:updated', onSelectionUpdated);
        targetCanvas.off('selection:cleared', onSelectionCleared);
      };
    };

    if (canvas.current) {
      unbind = bindListeners(canvas.current);
    } else {
      waitForCanvasTimer = setInterval(() => {
        if (!canvas.current || unbind) return;
        unbind = bindListeners(canvas.current);
        if (waitForCanvasTimer) {
          clearInterval(waitForCanvasTimer);
          waitForCanvasTimer = undefined;
        }
      }, 100);
    }

    return () => {
      if (waitForCanvasTimer) clearInterval(waitForCanvasTimer);
      if (unbind) unbind();
    };
  }, [canvas]);

  // Sync with parent setter if provided
  useEffect(() => {
    if (initialSetColorPalettes && initialColorPalettes) {
      setColorPalettes(initialColorPalettes);
    }
  }, [initialColorPalettes, initialSetColorPalettes]);

  const handleCanvasCleared = () => {
    setCurrentActiveLayer(-1);
    setSelectedLayerIndexes([]);
    setSelectedObjectType('');
    setFillColor('#000000');
    setStrokeColor('#000000');
    setStrokeWidth(0);
    setOpacity(100);
    setCornerRadius(0);
  };

  const handleSelectionCreated = () => {
    const activeObject = canvas.current?.getActiveObject() as any;
    if (activeObject) {
      const objects = canvas.current?.getObjects() || [];

      if (activeObject.type === 'activeSelection' && Array.isArray(activeObject._objects)) {
        const indexes = activeObject._objects
          .map((obj: any) => objects.indexOf(obj))
          .filter((index: number) => index > 0);
        setSelectedLayerIndexes(indexes);
        setCurrentActiveLayer(indexes.length > 0 ? indexes[indexes.length - 1] : -1);
      } else {
        const index = objects.indexOf(activeObject);
        const normalizedIndex = index > 0 ? index : -1;
        setSelectedLayerIndexes(normalizedIndex > 0 ? [normalizedIndex] : []);
        setCurrentActiveLayer(normalizedIndex);
      }

      setSelectedObjectType(activeObject.type || '');
      setFillColor(activeObject.fill || '#000000');
      setStrokeColor(activeObject.stroke || '#000000');
      setStrokeWidth(activeObject.strokeWidth || 0);
      setOpacity((activeObject.opacity || 1) * 100);
      setCornerRadius(activeObject.rx || 0);
      if (activeObject.type === 'textbox') {
        setFontSize(activeObject.fontSize || 12);
        const fontIdx = fonts.findIndex((f) => f.name === activeObject.fontFamily);
        setSelectedFontIndex(fontIdx >= 0 ? fontIdx : 0);
      }
    }
  };

  const handleModifiedObject = () => {
    const objects = canvas.current?.getObjects();
    if (objects && objects.length === 0) {
      setCurrentActiveLayer(-1);
      setSelectedLayerIndexes([]);
      setShapes([]);
    } else {
      setShapes([...(objects || [])]);
    }
  };

  const handleFabricObjectAdded = () => {
    const objects = canvas.current?.getObjects();
    setShapes([...(objects || [])]);
  };

  const handleLayerClick = (i: number, event?: ReactMouseEvent) => {
    if (!canvas.current) return;

    const activeObject = canvas.current?.getObjects()[i];
    if (!activeObject || i <= 0) return;

    const isShiftMultiSelect = Boolean(event?.shiftKey);

    if (!isShiftMultiSelect) {
      if (i === currentActiveLayer) {
        setCurrentActiveLayer(-1);
        setSelectedLayerIndexes([]);
        canvas.current.discardActiveObject().renderAll();
        return;
      }
      setCurrentActiveLayer(i);
      setSelectedLayerIndexes([i]);
      canvas.current.setActiveObject(activeObject).renderAll();
      return;
    }

    const objects = canvas.current.getObjects();
    const currentSelection = new Set(selectedLayerIndexes.filter((index) => index > 0));

    if (currentSelection.has(i)) {
      currentSelection.delete(i);
    } else {
      currentSelection.add(i);
    }

    const nextIndexes = Array.from(currentSelection).sort((a, b) => a - b);
    setSelectedLayerIndexes(nextIndexes);

    if (nextIndexes.length === 0) {
      setCurrentActiveLayer(-1);
      canvas.current.discardActiveObject().renderAll();
      return;
    }

    if (nextIndexes.length === 1) {
      const singleObject = objects[nextIndexes[0]];
      if (singleObject) {
        setCurrentActiveLayer(nextIndexes[0]);
        canvas.current.setActiveObject(singleObject).renderAll();
      }
      return;
    }

    const selectionObjects = nextIndexes.map((index) => objects[index]).filter(Boolean);
    const activeSelection = new fabric.ActiveSelection(selectionObjects, { canvas: canvas.current });
    setCurrentActiveLayer(nextIndexes[nextIndexes.length - 1]);
    canvas.current.setActiveObject(activeSelection).renderAll();
  };

  const handleLayerNameStartEdit = (index: number) => {
    const obj = canvas.current?.getObjects()[index] as any;
    if (!obj || index <= 0) return;

    const fallbackName = `Layer ${index}`;
    setEditingLayerIndex(index);
    setEditingLayerName(obj.name || fallbackName);
  };

  const handleLayerNameCommit = () => {
    if (!canvas.current || editingLayerIndex === null) return;

    const obj = canvas.current.getObjects()[editingLayerIndex] as any;
    if (!obj) {
      setEditingLayerIndex(null);
      setEditingLayerName('');
      return;
    }

    const trimmedName = editingLayerName.trim();
    obj.set('name', trimmedName || `Layer ${editingLayerIndex}`);
    canvas.current.requestRenderAll();
    handleModifiedObject();
    setEditingLayerIndex(null);
    setEditingLayerName('');
  };

  const handleLayerNameCancel = () => {
    setEditingLayerIndex(null);
    setEditingLayerName('');
  };

  const handleLayerDragStart = (event: React.DragEvent<HTMLLIElement>, index: number) => {
    if (index <= 0) return;
    event.dataTransfer.effectAllowed = 'move';
    event.dataTransfer.dropEffect = 'move';
    event.dataTransfer.setData('text/plain', String(index));
    setDraggingLayerIndex(index);
  };

  const handleLayerDrop = (event: React.DragEvent<HTMLLIElement>, targetIndex: number) => {
    event.preventDefault();
    if (!canvas.current) return;

    const dragIndexRaw = Number(event.dataTransfer.getData('text/plain'));
    const sourceIndex = Number.isFinite(dragIndexRaw) && dragIndexRaw > 0 ? dragIndexRaw : draggingLayerIndex;

    if (sourceIndex === null || sourceIndex === undefined) return;
    if (sourceIndex <= 0 || targetIndex <= 0) {
      setDraggingLayerIndex(null);
      setDragOverLayerIndex(null);
      return;
    }

    const objects = canvas.current.getObjects();
    const draggedObject = objects[sourceIndex];
    if (!draggedObject || sourceIndex === targetIndex) {
      setDraggingLayerIndex(null);
      setDragOverLayerIndex(null);
      return;
    }

    const displayLayers = objects
      .map((obj, index) => ({ obj, index }))
      .filter((entry) => entry.index > 0)
      .sort((a, b) => b.index - a.index);

    const sourceDisplayIndex = displayLayers.findIndex((entry) => entry.index === sourceIndex);
    const targetDisplayIndex = displayLayers.findIndex((entry) => entry.index === targetIndex);

    if (sourceDisplayIndex < 0 || targetDisplayIndex < 0) {
      setDraggingLayerIndex(null);
      setDragOverLayerIndex(null);
      return;
    }

    const originalDisplayOrder = displayLayers.map((entry) => entry.index);

    const reorderDisplayedLayers = (requestedInsertIndex: number) => {
      const nextLayers = [...displayLayers];
      const [movedLayer] = nextLayers.splice(sourceDisplayIndex, 1);
      let normalizedInsertIndex = requestedInsertIndex;
      if (sourceDisplayIndex < normalizedInsertIndex) normalizedInsertIndex -= 1;
      nextLayers.splice(normalizedInsertIndex, 0, movedLayer);
      return nextLayers;
    };

    const preferredInsertIndex = dragInsertPosition === 'before' ? targetDisplayIndex : targetDisplayIndex + 1;
    let reorderedDisplayLayers = reorderDisplayedLayers(preferredInsertIndex);

    // If drop resolves to the same order, force a move to the target row for deterministic behavior.
    const isNoOp = reorderedDisplayLayers.every((entry, idx) => entry.index === originalDisplayOrder[idx]);
    if (isNoOp && sourceDisplayIndex !== targetDisplayIndex) {
      reorderedDisplayLayers = reorderDisplayedLayers(targetDisplayIndex);
    }

    const ascendingLayers = [...reorderedDisplayLayers].reverse();
    ascendingLayers.forEach((entry, idx) => {
      canvas.current?.moveTo(entry.obj, idx + 1);
    });

    canvas.current.setActiveObject(draggedObject);
    canvas.current.requestRenderAll();

    const newIndex = canvas.current.getObjects().indexOf(draggedObject);
    setCurrentActiveLayer(newIndex);
    setSelectedLayerIndexes(newIndex > 0 ? [newIndex] : []);
    setDraggingLayerIndex(null);
    setDragOverLayerIndex(null);
    handleModifiedObject();
  };

  const handleLayerDragOver = (event: React.DragEvent<HTMLLIElement>, index: number) => {
    event.preventDefault();
    const bounds = event.currentTarget.getBoundingClientRect();
    const relativeY = event.clientY - bounds.top;
    const insertPosition = relativeY < bounds.height / 2 ? 'before' : 'after';

    setDragOverLayerIndex(index);
    setDragInsertPosition(insertPosition);
  };

  const handleDeleteSelectedLayersConfirm = (confirm: boolean) => {
    setDeleteDialogOpen(false);
    if (!confirm || !canvas.current) return;

    const selectedIndexes = selectedLayerIndexes.filter((index) => index > 0).sort((a, b) => b - a);
    if (selectedIndexes.length === 0) return;

    const objects = canvas.current.getObjects();
    selectedIndexes.forEach((index) => {
      const target = objects[index];
      if (target) canvas.current?.remove(target);
    });

    canvas.current.discardActiveObject();
    canvas.current.requestRenderAll();
    setSelectedLayerIndexes([]);
    setCurrentActiveLayer(-1);
    handleModifiedObject();
  };

  const toggleLayerLock = (index: number) => {
    const selectedObject: any = canvas.current?.getObjects()[index];
    if (!selectedObject) return;

    const isLocked = Boolean(selectedObject.lockMovementX);
    selectedObject.set({
      selectable: isLocked,
      lockMovementX: !isLocked,
      lockMovementY: !isLocked,
      lockRotation: !isLocked,
      lockScalingFlip: !isLocked,
      lockScalingY: !isLocked,
      lockScalingX: !isLocked,
      lockSkewingY: !isLocked,
      lockSkewingX: !isLocked,
    });
    canvas.current?.requestRenderAll();
    handleModifiedObject();
  };

  const toggleLayerVisibility = (index: number) => {
    const selectedObject: any = canvas.current?.getObjects()[index];
    if (!selectedObject) return;

    const isVisible = selectedObject.visible !== false;
    selectedObject.set({ visible: !isVisible, evented: !isVisible, selectable: !isVisible });
    if (isVisible && currentActiveLayer === index) {
      setCurrentActiveLayer(-1);
      canvas.current?.discardActiveObject().renderAll();
    } else {
      canvas.current?.requestRenderAll();
    }
    handleModifiedObject();
  };

  const moveLayer = (index: number, direction: 'up' | 'down') => {
    if (!canvas.current) return;

    const objects = canvas.current.getObjects();
    const selectedObject = objects[index];
    if (!selectedObject) return;

    // Index 0 is the background layer and should never be crossed.
    if (index <= 0) return;

    const maxIndex = objects.length - 1;
    const targetIndex = direction === 'up' ? Math.min(index + 1, maxIndex) : Math.max(index - 1, 1);

    if (targetIndex === index) return;

    canvas.current.moveTo(selectedObject, targetIndex);
    canvas.current.setActiveObject(selectedObject);
    canvas.current.requestRenderAll();
    setCurrentActiveLayer(targetIndex);
    handleModifiedObject();
  };

  const handleFillColorChange = (color: string) => {
    setFillColor(color);
    const activeObject = canvas.current?.getActiveObject() as any;
    if (activeObject) {
      activeObject.set('fill', color);
      canvas.current?.renderAll();
    }
  };

  const handleStrokeColorChange = (color: string) => {
    setStrokeColor(color);
    const activeObject = canvas.current?.getActiveObject() as any;
    if (activeObject) {
      activeObject.set('stroke', color);
      canvas.current?.renderAll();
    }
  };

  const handleStrokeWidthChange = (e: ChangeEvent<HTMLInputElement>) => {
    const value = parseInt(e.target.value) || 0;
    setStrokeWidth(value);
    const activeObject = canvas.current?.getActiveObject() as any;
    if (activeObject) {
      activeObject.set('strokeWidth', value);
      canvas.current?.renderAll();
    }
  };

  const handleOpacityChange = (event: any, value: number | number[]) => {
    const opacityValue = Array.isArray(value) ? value[0] : value;
    setOpacity(opacityValue);
    const activeObject = canvas.current?.getActiveObject() as any;
    if (activeObject) {
      activeObject.set('opacity', opacityValue / 100);
      canvas.current?.renderAll();
    }
  };

  const handleCornerRadiusChange = (e: ChangeEvent<HTMLInputElement>) => {
    const value = parseInt(e.target.value) || 0;
    setCornerRadius(value);
    const activeObject = canvas.current?.getActiveObject() as any;
    if (activeObject && (activeObject.type === 'rect' || activeObject.type === 'triangle')) {
      activeObject.set({ rx: value, ry: value });
      canvas.current?.renderAll();
    }
  };

  const handleFontFamilyChange = (font: string) => {
    const activeObject = canvas.current?.getActiveObject() as any;
    if (activeObject && activeObject.type === 'textbox') {
      activeObject.set('fontFamily', font);
      canvas.current?.renderAll();
    }
  };

  const handleFontSizeChange = (e: ChangeEvent<HTMLInputElement>) => {
    const value = parseInt(e.target.value) || 12;
    setFontSize(value);
    const activeObject = canvas.current?.getActiveObject() as any;
    if (activeObject && activeObject.type === 'textbox') {
      activeObject.set('fontSize', value);
      canvas.current?.renderAll();
    }
  };

  const handleAlignClick = (alignment: string) => {
    const activeObject: Textbox | undefined = canvas.current?.getActiveObject() as any;
    if (activeObject && activeObject.type === 'textbox') {
      activeObject.textAlign = alignment;
      canvas.current?.renderAll();
    }
  };

  const handleUnderline = () => {
    const activeObject: Textbox | undefined = canvas.current?.getActiveObject() as any;
    if (activeObject && activeObject.type === 'textbox') {
      activeObject.underline = !activeObject.underline;
      canvas.current?.renderAll();
    }
  };

  const layersWithIndex = (shapes || []).map((shape, index) => ({ shape, index }));
  const editableLayers = layersWithIndex.filter((entry) => entry.index > 0).reverse();
  const hasSelection = Boolean(selectedObjectType);
  const brandPalette = colorPalettes.find((palette) => palette?.title?.toLowerCase() === 'brand colors');
  const defaultBrandColors = DEFAULT_BRAND_COLOR_PALETTES[0].colors.map(normalizeHex);
  const currentBrandColors = (brandPalette?.colors || []).map(normalizeHex);
  const isCustomBrandPalette =
    currentBrandColors.length > 0 &&
    (currentBrandColors.length !== defaultBrandColors.length ||
      currentBrandColors.some((color, index) => color !== defaultBrandColors[index]));
  const quickFillColors = isCustomBrandPalette ? currentBrandColors : STANDARD_FILL_SWATCHES;
  const normalizedSearch = searchQuery.trim().toLowerCase();
  const filteredEditableLayers =
    normalizedSearch.length === 0
      ? editableLayers
      : editableLayers.filter(({ shape, index }) => {
          const obj = shape as any;
          const haystack = `${obj?.name || ''} ${obj?.type || ''} layer ${index}`.toLowerCase();
          return haystack.includes(normalizedSearch);
        });

  if (containerRef == null) return <Loader />;
  return (
    <Box
      ref={containerRef}
      sx={{
        position: 'relative',
        backgroundColor: '#2a2c31',
        height: '100%',
        minHeight: 0,
        overflow: 'hidden',
        display: 'grid',
        gridTemplateRows: hasSelection
          ? 'auto auto auto minmax(220px, 1fr)'
          : 'auto auto minmax(260px, 1fr)',
      }}
    >
      {/* Selected Shape Section */}
      {selectedObjectType && (
        <Box
          sx={{
            width: '100%',
            p: 1.1,
            bgcolor: '#1a1c1f',
            borderBottom: '1px solid #444',
            minHeight: 0,
            maxHeight: '36vh',
            overflowY: 'auto',
            scrollbarWidth: 'none',
            msOverflowStyle: 'none',
            '&::-webkit-scrollbar': {
              width: 0,
              height: 0,
            },
          }}
        >
          <Typography sx={{ color: '#fff', fontWeight: 700, fontSize: '0.95rem', mb: 1.1 }}>
            Selected Shape: {selectedObjectType.charAt(0).toUpperCase() + selectedObjectType.slice(1)}
          </Typography>

          {/* Fill Section */}
          <Box sx={{ mb: 1.2 }}>
            <Typography sx={{ color: '#bbb', fontSize: '0.82rem', mb: 0.6 }}>Fill</Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box
                sx={{
                  width: 32,
                  height: 32,
                  bgcolor: fillColor,
                  border: '2px solid #555',
                  borderRadius: 1,
                  cursor: 'pointer',
                }}
                onClick={() => setFillColorPickerOpen(!fillColorPickerOpen)}
              />
              {!fillColorPickerOpen && (
                <Stack direction='row' spacing={0.5} sx={{ flexWrap: 'wrap', rowGap: 0.5, minHeight: 32 }}>
                  {quickFillColors.slice(0, 8).map((color) => {
                    const isActive = normalizeHex(fillColor) === normalizeHex(color);
                    return (
                      <Box
                        key={`quick-fill-${color}`}
                        onClick={() => handleFillColorChange(color)}
                        sx={{
                          width: 18,
                          height: 18,
                          borderRadius: '50%',
                          bgcolor: color,
                          border: isActive ? '2px solid #f2f5fb' : '1px solid #596170',
                          cursor: 'pointer',
                        }}
                        title={isCustomBrandPalette ? 'Brand color' : 'Standard color'}
                      />
                    );
                  })}
                </Stack>
              )}
              {fillColorPickerOpen && colorPalettes && setColorPalettes && (
                <Box sx={{ flex: 1 }}>
                  <ColorPicker
                    handleChange={handleFillColorChange}
                    activeColor={fillColor}
                    colorPalettes={colorPalettes}
                    setColorPalettes={setColorPalettes}
                  />
                </Box>
              )}
            </Box>
          </Box>

          {/* Stroke Section */}
          <Box sx={{ mb: 1.2 }}>
            <Typography sx={{ color: '#bbb', fontSize: '0.82rem', mb: 0.6 }}>Stroke</Typography>
            <Stack direction="row" gap={1} alignItems="center">
              <TextField
                type="number"
                value={strokeWidth}
                onChange={handleStrokeWidthChange}
                size="small"
                sx={{ width: 52, backgroundColor: '#333' }}
                inputProps={{ style: { color: '#fff' } }}
              />
              <Select
                value={selectedObjectType}
                disabled
                size="small"
                sx={{ width: 86, backgroundColor: '#333', color: '#fff' }}
              >
                <MenuItem value={selectedObjectType}>{selectedObjectType}</MenuItem>
              </Select>
            </Stack>
          </Box>

          {/* Opacity Section */}
          <Box sx={{ mb: 1.1 }}>
            <Stack direction="row" alignItems="center" gap={1}>
              <Typography sx={{ color: '#bbb', fontSize: '0.82rem', minWidth: 52 }}>Opacity</Typography>
              <Slider
                value={opacity}
                onChange={handleOpacityChange}
                min={0}
                max={100}
                sx={{ flex: 1, color: '#8cb9ff' }}
              />
              <Typography sx={{ color: '#fff', fontSize: '0.82rem', minWidth: 34 }}>{opacity}%</Typography>
            </Stack>
          </Box>

          {/* Group / Ungroup Section */}
          <Box sx={{ mb: 1.1 }}>
            <FormControlLabel
              control={<Switch sx={{ color: '#8cb9ff' }} />}
              label="Group / Ungroup"
              sx={{ color: '#bbb', '& .MuiFormControlLabel-label': { fontSize: '0.8rem' } }}
            />
          </Box>

          {/* Text Alignment Section - Only for Textbox */}
          {selectedObjectType === 'textbox' && (
            <Box sx={{ mb: 1.2 }}>
              <Typography sx={{ color: '#bbb', fontSize: '0.82rem', mb: 0.6 }}>Text / Alignment</Typography>
              <Stack direction="row" gap={0.5} sx={{ mb: 1 }}>
                <Button
                  size="small"
                  onClick={() => handleAlignClick('left')}
                  sx={{
                    bgcolor: '#333',
                    color: '#fff',
                    flex: 1,
                    '&:hover': { bgcolor: '#444' },
                  }}
                >
                  L
                </Button>
                <Button
                  size="small"
                  onClick={() => handleAlignClick('center')}
                  sx={{
                    bgcolor: '#333',
                    color: '#fff',
                    flex: 1,
                    '&:hover': { bgcolor: '#444' },
                  }}
                >
                  C
                </Button>
                <Button
                  size="small"
                  onClick={() => handleAlignClick('right')}
                  sx={{
                    bgcolor: '#333',
                    color: '#fff',
                    flex: 1,
                    '&:hover': { bgcolor: '#444' },
                  }}
                >
                  R
                </Button>
                <Button
                  size="small"
                  onClick={handleUnderline}
                  sx={{
                    bgcolor: '#333',
                    color: '#fff',
                    flex: 1,
                    '&:hover': { bgcolor: '#444' },
                  }}
                >
                  U
                </Button>
              </Stack>
              <Stack direction="row" gap={1}>
                <SelectFont
                  options={fonts}
                  handleSelect={handleFontFamilyChange}
                  selected={selectedFontIndex}
                />
                <TextField
                  type="number"
                  value={fontSize}
                  onChange={handleFontSizeChange}
                  size="small"
                  sx={{ width: 52, backgroundColor: '#333' }}
                  inputProps={{ style: { color: '#fff' } }}
                />
              </Stack>
            </Box>
          )}

          {/* Corner Radius Section - Only for Rect/Triangle */}
          {(selectedObjectType === 'rect' || selectedObjectType === 'triangle') && (
            <Box sx={{ mb: 1.1 }}>
              <Typography sx={{ color: '#bbb', fontSize: '0.82rem', mb: 0.6 }}>Corner Radius</Typography>
              <TextField
                type="number"
                value={cornerRadius}
                onChange={handleCornerRadiusChange}
                size="small"
                sx={{ width: '100%', backgroundColor: '#333' }}
                inputProps={{ style: { color: '#fff' }, min: 0 }}
              />
            </Box>
          )}

        </Box>
      )}

      {/* Layers Section Header */}
      <Box
        sx={{
          bgcolor: '#171719',
          background: 'linear-gradient(180deg, #1a1c20 0%, #171719 100%)',
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 1.1,
          py: 0.65,
          borderBottom: '1px solid #30323a',
        }}
      >
        <Stack direction='row' spacing={1} alignItems='center'>
          <Typography color={'#fff'} sx={{ fontWeight: 700, fontSize: '0.94rem', letterSpacing: 0.2 }}>
            Layers
          </Typography>
          {selectedLayerIndexes.length > 0 && (
            <Box
              sx={{
                px: 0.65,
                py: 0.1,
                borderRadius: 99,
                bgcolor: '#2f3238',
                border: '1px solid #484d57',
                color: '#fff',
                fontSize: '0.62rem',
                fontWeight: 700,
              }}
            >
              {selectedLayerIndexes.length} selected
            </Box>
          )}
        </Stack>
        <IconButton
          size='small'
          disabled={selectedLayerIndexes.length === 0}
          onClick={() => setDeleteDialogOpen(true)}
          sx={{ color: selectedLayerIndexes.length === 0 ? '#666' : '#ff8080' }}
          title='Delete selected layers'
        >
          <DeleteOutline />
        </IconButton>
      </Box>

      <Box
        sx={{
          px: 1.1,
          pt: 0.7,
          pb: 0.7,
          bgcolor: '#171719',
          flexShrink: 0,
          borderBottom: '1px solid #2b2f37',
        }}
      >
        <TextField
          placeholder='Search layers...'
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          size='small'
          fullWidth
          InputProps={{
            startAdornment: (
              <InputAdornment position='start'>
                <SearchRoundedIcon sx={{ color: '#8f95a3', fontSize: 16 }} />
              </InputAdornment>
            ),
          }}
          sx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: 2,
              backgroundColor: '#22252b',
              minHeight: 36,
            },
            '& .MuiOutlinedInput-input': { color: '#fff' },
            '& .MuiOutlinedInput-input::placeholder': { color: '#888', opacity: 1 },
          }}
        />
      </Box>

      {/* Layers List */}
      <Box
        sx={{
          minHeight: 0,
          overflowY: 'auto',
          overflowX: 'hidden',
          pb: 1,
          bgcolor: '#2a2c31',
          scrollbarGutter: 'stable',
        }}
      >
        {shapes && (
          <List
            sx={{
              display: 'flex',
              flexDirection: 'column',
              width: '100%',
              listStyle: 'none',
              scrollbarWidth: 'thin',
              padding: '7px 6px',
              gap: '6px',
              alignItems: 'center',
            }}
          >
            {shapes.length <= 1 && (
              <ListItem
                style={{
                  width: '90%',
                  color: '#bbb',
                  justifyContent: 'center',
                  padding: '12px',
                }}
              >
                <ListItemText
                  primary='No layers yet'
                  secondary='Add shapes, text, or assets to populate this list.'
                  primaryTypographyProps={{ sx: { color: '#f2f5fb', fontWeight: 600 } }}
                  secondaryTypographyProps={{ sx: { color: '#c4cada' } }}
                />
              </ListItem>
            )}
            {shapes.length > 1 && filteredEditableLayers.length === 0 && (
              <ListItem
                style={{
                  width: '90%',
                  color: '#bbb',
                  justifyContent: 'center',
                  padding: '12px',
                }}
              >
                <ListItemText
                  primary='No matching layers'
                  secondary='Try a different search term.'
                  primaryTypographyProps={{ sx: { color: '#f2f5fb', fontWeight: 600 } }}
                  secondaryTypographyProps={{ sx: { color: '#c4cada' } }}
                />
              </ListItem>
            )}
            {filteredEditableLayers.map(({ shape, index }: { shape: CanvasObject; index: number }) => {
                if (canvas.current) {
                  const shapeAny = shape as any;
                  const isGrouped =
                    shapeAny?.type === 'group' ||
                    (Array.isArray(shapeAny?._objects) && shapeAny._objects.length > 1);
                  const isSelected = selectedLayerIndexes.includes(index);
                  const isActive = index === currentActiveLayer;
                  const miniCanvasData = {
                    ...canvas.current?.toJSON(),
                    background: canvas.current
                      ? typeof canvas.current.backgroundColor === 'string'
                        ? canvas.current.backgroundColor
                        : '#fff'
                      : '#fff',
                  };
                  return (
                    <ListItem
                      key={`${shapeAny?.name || shapeAny?.type || 'layer'}-${index}`}
                      onClick={(event) => handleLayerClick(index, event)}
                      onDoubleClick={() => handleLayerNameStartEdit(index)}
                      draggable
                      onDragStart={(event) => handleLayerDragStart(event, index)}
                      onDragStartCapture={(event) => handleLayerDragStart(event, index)}
                      onDragEnd={() => {
                        setDraggingLayerIndex(null);
                        setDragOverLayerIndex(null);
                      }}
                      onDragEnter={(event) => event.preventDefault()}
                      onDragOver={(event) => handleLayerDragOver(event, index)}
                      onDragOverCapture={(event) => handleLayerDragOver(event, index)}
                      onDragLeave={() => setDragOverLayerIndex(null)}
                      onDrop={(event) => handleLayerDrop(event, index)}
                      onDropCapture={(event) => handleLayerDrop(event, index)}
                      style={{
                        background:
                          isActive
                            ? 'linear-gradient(135deg, rgba(255,105,97,0.92) 0%, rgba(255,141,87,0.86) 100%)'
                            : isSelected
                              ? '#3b3334'
                              : '#232730',
                        marginBottom: '8px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        padding: 5,
                        minHeight: '74px',
                        width: '92%',
                        borderRadius: 9,
                        border: isActive ? '1px solid rgba(255, 191, 168, 0.9)' : '1px solid #3f4550',
                        boxShadow:
                          dragOverLayerIndex === index && dragInsertPosition === 'before'
                            ? 'inset 0 2px 0 #8cb9ff, 0 3px 8px rgba(0,0,0,0.25)'
                            : dragOverLayerIndex === index && dragInsertPosition === 'after'
                              ? 'inset 0 -2px 0 #8cb9ff, 0 3px 8px rgba(0,0,0,0.25)'
                              : draggingLayerIndex === index
                                ? 'inset 0 0 0 1px #8cb9ff, 0 3px 8px rgba(0,0,0,0.25)'
                                : '0 3px 8px rgba(0,0,0,0.25)',
                        cursor: 'grab',
                        opacity: draggingLayerIndex === index ? 0.7 : 1,
                      }}
                    >
                      <Grid container width={'100%'} height={'100%'}>
                        <Grid item xs={8} height={'100%'}>
                          <Stack direction='row' alignItems='center' spacing={0.75} sx={{ mb: 0.5 }}>
                            <DragIndicatorIcon sx={{ color: '#9aa4b5', fontSize: 12 }} />
                            {editingLayerIndex === index ? (
                              <TextField
                                size='small'
                                value={editingLayerName}
                                onChange={(event) => setEditingLayerName(event.target.value)}
                                onBlur={handleLayerNameCommit}
                                onKeyDown={(event) => {
                                  if (event.key === 'Enter') {
                                    event.preventDefault();
                                    handleLayerNameCommit();
                                  }
                                  if (event.key === 'Escape') {
                                    event.preventDefault();
                                    handleLayerNameCancel();
                                  }
                                }}
                                autoFocus
                                sx={{
                                  width: '100%',
                                  '& .MuiOutlinedInput-input': { color: '#fff', py: '2px' },
                                }}
                              />
                            ) : (
                              <Typography sx={{ color: '#fff', fontSize: '0.69rem', fontWeight: 600 }}>
                                {shapeAny?.name || `Layer ${index}`}
                              </Typography>
                            )}
                            {isGrouped && (
                              <Box
                                sx={{
                                  px: 0.75,
                                  py: 0.15,
                                  borderRadius: 1,
                                  fontSize: '0.625rem',
                                  color: '#111',
                                  bgcolor: '#ffad42',
                                  fontWeight: 700,
                                }}
                              >
                                GROUP
                              </Box>
                            )}
                          </Stack>
                          <MiniCanvas
                            ogHeight={canvas.current?.height}
                            ogWidth={canvas.current?.width}
                            data={miniCanvasData}
                            index={index}
                            shape={shape}
                            zoom={canvas.current?.getZoom()}
                          />
                        </Grid>
                        <Grid item xs={4} display={'flex'} flexDirection={'column'} justifyContent={'space-evenly'}>
                          <IconButton
                            size='small'
                            onClick={(event) => {
                              event.stopPropagation();
                              moveLayer(index, 'up');
                            }}
                          >
                            <VerticalAlignTopIcon sx={{ color: '#fff', fontSize: 16 }} />
                          </IconButton>
                          <IconButton
                            size='small'
                            disabled={index <= 1}
                            onClick={(event) => {
                              event.stopPropagation();
                              moveLayer(index, 'down');
                            }}
                          >
                            <VerticalAlignBottomIcon
                              sx={{
                                color: index <= 1 ? '#666' : '#fff',
                                fontSize: 14,
                              }}
                            />
                          </IconButton>
                          <IconButton
                            size='small'
                            onClick={(event) => {
                              event.stopPropagation();
                              toggleLayerLock(index);
                            }}
                          >
                            {(shape as any)?.lockMovementX ? (
                              <LockIcon sx={{ color: '#ffad42', fontSize: 14 }} />
                            ) : (
                              <LockOpenIcon sx={{ color: '#8cb9ff', fontSize: 14 }} />
                            )}
                          </IconButton>
                          <IconButton
                            size='small'
                            onClick={(event) => {
                              event.stopPropagation();
                              toggleLayerVisibility(index);
                            }}
                          >
                            {(shape as any)?.visible === false ? (
                              <VisibilityOffIcon sx={{ color: '#ff8080', fontSize: 14 }} />
                            ) : (
                              <VisibilityIcon sx={{ color: '#9be7a1', fontSize: 14 }} />
                            )}
                          </IconButton>
                        </Grid>
                      </Grid>
                    </ListItem>
                  );
                }
              })}

            {shapes.length > 0 && (
              <ListItem
                style={{
                  width: '92%',
                  color: '#cfd3dc',
                  justifyContent: 'space-between',
                  padding: '7px 9px',
                  border: '1px solid #3a3f49',
                  borderRadius: 9,
                  background: '#1f232b',
                  opacity: 0.84,
                }}
              >
                <ListItemText
                  primary='Background'
                  secondary='Base layer (locked order)'
                  primaryTypographyProps={{ sx: { color: '#f2f5fb', fontWeight: 600 } }}
                  secondaryTypographyProps={{ sx: { color: '#c4cada' } }}
                />
              </ListItem>
            )}
          </List>
        )}
      </Box>

      <AlertDialog
        open={deleteDialogOpen}
        handleClose={handleDeleteSelectedLayersConfirm}
        title='Delete selected layer(s)?'
        contentText='This will permanently remove the selected layers from the canvas.'
        confirmBtnText='Delete'
        rejectBtnText='Cancel'
      />
    </Box>
  );
};

export default Layers;
